import { FIRST_LOAD_STATE_WAIT_MS } from 'projects/service/src/app/shared/utils/grid-persistence';
import { GridConfig } from '@inavista/components/grid';

// Cycle Counts popup → Open Exceptions on @inavista/components. Same checkbox column, columns, labels, order, widths,
// status pills, links (LN Shipment No, Item, Description) and toolbar (Columns, Create Adjustment Order, Search, Excel
// export, Clear filters) as the Syncfusion grid it replaces. Rows are the popup's own openExceptionList
// (LocalRowsGridApi). No paging (Martin).

export const CYCLE_COUNT_OPEN_EXCEPTION_URL = 'page://cycle-count-open-exception';
export const CYCLE_COUNT_OPEN_EXCEPTION_PERSISTENCE_KEY = 'rims.service.cycle-count-popup-open-exceptions';
export const CREATE_ADJUSTMENT_ORDER_ACTION = 'create-adjust-order';

// fas fa-balance-scale
const SCALE_ICON = '<line x1="12" y1="3" x2="12" y2="21" /><line x1="7" y1="21" x2="17" y2="21" /><line x1="4" y1="7" x2="20" y2="7" /><path d="M4 7l-3 7a3 3 0 0 0 6 0Z" /><path d="M20 7l-3 7a3 3 0 0 0 6 0Z" />';

export const CYCLE_COUNT_OPEN_EXCEPTION_COLUMNS: [string, string][] = [
  ['__rowKey', 'int'],
  ['shipmentOrderId', 'nvarchar'],
  ['status', 'nvarchar'],
  ['warehouseOrderLN', 'nvarchar'],
  ['itemCode', 'nvarchar'],
  ['itemDescription', 'nvarchar'],
  ['quantity', 'nvarchar'],
  ['quantitySent', 'nvarchar'],
  ['quantityReceived', 'nvarchar'],
  ['reasonDescription', 'nvarchar'],
  ['exceptionStatus', 'nvarchar'],
];

const SHOWN = CYCLE_COUNT_OPEN_EXCEPTION_COLUMNS.map(([c]) => c).filter(c => c !== '__rowKey');

export function buildCycleCountOpenExceptionGridConfig(statusColor: (value: unknown) => string, createDisabledReason: () => string | null): GridConfig {
  return {
    apiBaseUrl: CYCLE_COUNT_OPEN_EXCEPTION_URL,
    clientSide: true,
    // The page numbers the rows (LN Shipment No repeats when a shipment has several exceptions).
    keyColumn: '__rowKey',
    rowHeight: 30,
    // The old checkbox column (multi-select, checkbox only), 40 wide.
    selectable: true,
    selectColumnWidth: 40,
    defaultColumnOrder: SHOWN,
    hiddenByDefaultColumns: ['__rowKey'],
    columnLabels: {
      shipmentOrderId: 'LN Shipment No', status: 'Status', warehouseOrderLN: 'LN Warehouse No', itemCode: 'Item',
      itemDescription: 'Description', quantity: 'Qty Ordered', quantitySent: 'Qty Sent', quantityReceived: 'Qty Received',
      reasonDescription: 'Reason', exceptionStatus: 'Exception status',
    },
    // Every column left, as the old grid.
    columnAlign: Object.fromEntries(SHOWN.map(c => [c, { horizontal: 'left' as const, header: { horizontal: 'left' as const } }])),
    // Every old column was 100 wide, filling the popup.
    columnWidths: Object.fromEntries(SHOWN.map(c => [c, 100])),
    stretchColumns: true,
    hyperlinkColumns: ['shipmentOrderId', 'itemCode', 'itemDescription'],
    columnColors: { status: statusColor, reasonDescription: statusColor, exceptionStatus: statusColor },
    toolbarActions: [{
      key: CREATE_ADJUSTMENT_ORDER_ACTION, label: 'Create Adjustment Order', kind: 'button', icon: SCALE_ICON,
      disabled: () => !!createDisabledReason(), disabledReason: () => createDisabledReason() ?? '',
    }],
    // The old toolbar: Columns, Refresh, Create Adjustment Order, Search, Excel export (every user), Clear filters.
    // Refresh is not kept: it called an empty getData() and never did anything.
    enableColumnsPicker: true,
    enableExport: true,
    enableSavedViews: false,
    enableScrollButtons: false,
    enableFitColumns: false,
    enableResetLayout: true,
    enableRefresh: false,
    enableFooter: false,
    multiSort: true,
    emptyStateMessage: 'No open exceptions.',
    // Right-click a cell filters by its value (Martin, 2026-10-29: every grid that filters).
    enableContextMenuFilter: true,
    persistenceKey: CYCLE_COUNT_OPEN_EXCEPTION_PERSISTENCE_KEY,
    firstLoadStateWaitMs: FIRST_LOAD_STATE_WAIT_MS,
  };
}
